import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../services/api'

/** 届け先。`orders` 公開 doc には無く、API 経由でのみ得られる (#1025)。 */
export interface DeliveryDetail {
  deliveryAddress?: string
  deliveryNotes?: string
  deliveryLat?: number
  deliveryLng?: number
}

/** hydrate 対象の最小形。`Order` でも店舗側の行データでも受けられるようにする。 */
interface OrderLike extends DeliveryDetail {
  id: string
  orderType?: string
}

/**
 * #1025 出前の届け先を注文オブジェクトに合成して返す。
 *
 * 住所・座標・受け渡しメモは `orders/{id}/private/delivery` (firestore.rules で
 * read closed) にあり、**画面が使っている onSnapshot では取れない**。公開 doc に
 * 置くと、同じ doc の `customerName` と突き合わせて「氏名 + 自宅住所 + 座標」が
 * 未認証クライアントから読めてしまうため分離した。
 *
 * ここで注入して返すので、**描画側は今までどおり `order.deliveryAddress` を
 * 読むだけでよい**（呼び出し箇所を書き換えずに済む）。
 *
 * 取得は orderId 単位で 1 回だけ。出前は 1 注文 1 件で件数も少ないため、
 * まとめ取り API は用意していない。
 */
export function useDeliveryDetails<T extends OrderLike>(
  orders: T[],
  role: 'customer' | 'owner',
  userId?: string | null,
): T[] {
  const [details, setDetails] = useState<Record<string, DeliveryDetail>>({})
  // 取得済み / 取得中の orderId。**失敗した ID も入れて再試行しない** —
  // 403/404 が返る注文を毎 render で叩き続けると無駄な往復になる。
  const requested = useRef<Set<string>>(new Set())

  // 取得候補 = 届け先がまだ無い出前注文。**既取得の除外はここでやらない** —
  // requested は ref なので render 中に読むと ESLint (react-hooks/refs) に
  // 引っかかるうえ、mutable な値を描画結果に混ぜることになる。除外は effect 側。
  // 依存を安定させるため ID の並びを文字列化して比較する。
  const candidateKey = useMemo(
    () => orders
      .filter((o) => o.orderType === 'delivery' && !o.deliveryAddress && o.id)
      .map((o) => o.id)
      .join(','),
    [orders],
  )

  useEffect(() => {
    if (!candidateKey) return
    const ids = candidateKey.split(',').filter((id) => !requested.current.has(id))
    if (!ids.length) return
    let cancelled = false
    ids.forEach((id) => requested.current.add(id))
    void (async () => {
      for (const id of ids) {
        try {
          const res = role === 'owner'
            ? await api.getOwnerDeliveryDetail(id)
            : await api.getDeliveryDetail(id, userId || undefined)
          if (cancelled) return
          if (res && !res.detail) {
            setDetails((prev) => ({ ...prev, [id]: res as DeliveryDetail }))
          }
        } catch (e) {
          console.error('[useDeliveryDetails] fetch failed:', id, e)
        }
      }
    })()
    return () => { cancelled = true }
  }, [candidateKey, role, userId])

  return useMemo(
    () => orders.map((o) => (details[o.id] ? { ...o, ...details[o.id] } : o)),
    [orders, details],
  )
}
